import { uuid } from "functions/util";

/**
 * Local assessment interface for job creation (before saving to DB)
 */
export interface ILocalAssessment {
    assessmentId: string; // temporary UUID for local management
    name: string;
    resultsToStageRule: CompleoShared.Assessments.IResultsToStageRule[];
    tests: ILocalTest[];
}

/**
 * Local test interface for job creation
 */
export interface ILocalTest {
    testId: string;
    testName: string;
    testType: CompleoShared.Assessments.testType;
    possibleResults?: CompleoShared.Assessments.IPossibleResults[];
    answersToResultsNameToCalculateScore?: string;
    compleoModelId?: string; // if added from template
    templateUUID?: string; // if added from template
}

/**
 * Generate a temporary assessment ID
 */
export const generateTempAssessmentId = (): string => {
    return `temp-assessment-${uuid()}`;
};

/**
 * Pipeline stage interface (minimal)
 */
interface IPipelineStage {
    id: string;
    name: string;
}

/**
 * Validate if a stage ID exists in the current pipeline stages
 */
export const validateStageExists = (
    stageId: string,
    pipelineStages: IPipelineStage[]
): boolean => {
    if (!pipelineStages || pipelineStages.length === 0) return false;
    return pipelineStages.some((stage) => stage.id === stageId);
};

/**
 * Get invalid stage rules (stages that don't exist in pipeline)
 */
export const getInvalidStageRules = (
    resultsToStageRule: CompleoShared.Assessments.IResultsToStageRule[],
    pipelineStages: IPipelineStage[]
): CompleoShared.Assessments.IResultsToStageRule[] => {
    if (!resultsToStageRule || !pipelineStages) return [];
    return resultsToStageRule.filter(
        (rule) => !validateStageExists(rule.targetStage.value, pipelineStages)
    );
};

/**
 * Remove invalid stage rules (stages that don't exist in pipeline)
 * Returns only the valid rules
 */
export const removeInvalidStageRules = (
    resultsToStageRule: CompleoShared.Assessments.IResultsToStageRule[],
    pipelineStages: IPipelineStage[]
): CompleoShared.Assessments.IResultsToStageRule[] => {
    if (!resultsToStageRule || !pipelineStages) return resultsToStageRule || [];
    return resultsToStageRule.filter((rule) =>
        validateStageExists(rule.targetStage.value, pipelineStages)
    );
};

/**
 * Clean up all assessments by removing invalid stage rules
 */
export const cleanupInvalidStageRulesFromAssessments = (
    assessments: ILocalAssessment[],
    pipelineStages: IPipelineStage[]
): ILocalAssessment[] => {
    return assessments.map((assessment) => ({
        ...assessment,
        resultsToStageRule: removeInvalidStageRules(
            assessment.resultsToStageRule,
            pipelineStages
        )
    }));
};

/**
 * Check if a test is already added to an assessment
 */
export const isTestAlreadyAdded = (
    testId: string,
    testName: string,
    existingTests: ILocalTest[]
): boolean => {
    return existingTests.some(
        (test) => test.testId === testId || test.testName === testName
    );
};

/**
 * Filter available tests (exclude already added ones)
 */
export const filterAvailableTests = (
    allTests: CompleoShared.Assessments.ITest[],
    existingTests: ILocalTest[]
): CompleoShared.Assessments.ITest[] => {
    return allTests.filter(
        (test) => !isTestAlreadyAdded(test.skGS1pk, test.name, existingTests)
    );
};

/**
 * Filter available templates (exclude already added ones)
 */
export const filterAvailableTemplates = (
    allTemplates: CompleoShared.Assessments.ITestTemplate[],
    existingTests: ILocalTest[]
): CompleoShared.Assessments.ITestTemplate[] => {
    return allTemplates.filter(
        (template) =>
            !isTestAlreadyAdded(
                template.compleoModelId || "",
                template.name,
                existingTests
            )
    );
};

/**
 * Convert test template to local test format
 */
export const templateToLocalTest = (
    template: CompleoShared.Assessments.ITestTemplate,
    templateUUID: string,
    answersToResultsName?: string
): ILocalTest => {
    return {
        testId: templateUUID,
        testName: template.name,
        testType: template.testType,
        possibleResults: template.possibleResults,
        answersToResultsNameToCalculateScore: answersToResultsName,
        compleoModelId: templateUUID,
        templateUUID: templateUUID
    };
};

/**
 * Convert test to local test format
 */
export const testToLocalTest = (
    test: CompleoShared.Assessments.ITest,
    answersToResultsName?: string
): ILocalTest => {
    return {
        testId: test.skGS1pk,
        testName: test.name,
        testType: test.testType,
        possibleResults: test.possibleResults,
        answersToResultsNameToCalculateScore: answersToResultsName,
        compleoModelId: test.compleoModelId
    };
};

/**
 * Add a test to an assessment
 */
export const addTestToAssessment = (
    assessments: ILocalAssessment[],
    assessmentId: string,
    test: ILocalTest
): ILocalAssessment[] => {
    return assessments.map((assessment) => {
        if (assessment.assessmentId === assessmentId) {
            return {
                ...assessment,
                tests: [...assessment.tests, test]
            };
        }
        return assessment;
    });
};

/**
 * Remove a test from an assessment
 */
export const removeTestFromAssessment = (
    assessments: ILocalAssessment[],
    assessmentId: string,
    testId: string
): ILocalAssessment[] => {
    return assessments.map((assessment) => {
        if (assessment.assessmentId === assessmentId) {
            return {
                ...assessment,
                tests: assessment.tests.filter((test) => test.testId !== testId)
            };
        }
        return assessment;
    });
};

/**
 * Update an assessment
 */
export const updateAssessment = (
    assessments: ILocalAssessment[],
    assessmentId: string,
    updates: Partial<ILocalAssessment>
): ILocalAssessment[] => {
    return assessments.map((assessment) => {
        if (assessment.assessmentId === assessmentId) {
            return {
                ...assessment,
                ...updates,
                assessmentId: assessment.assessmentId // preserve the ID
            };
        }
        return assessment;
    });
};

/**
 * Delete an assessment
 */
export const deleteAssessment = (
    assessments: ILocalAssessment[],
    assessmentId: string
): ILocalAssessment[] => {
    return assessments.filter(
        (assessment) => assessment.assessmentId !== assessmentId
    );
};

/**
 * Create a new empty assessment
 */
export const createNewAssessment = (
    name: string,
    resultsToStageRule: CompleoShared.Assessments.IResultsToStageRule[] = []
): ILocalAssessment => {
    return {
        assessmentId: generateTempAssessmentId(),
        name,
        resultsToStageRule,
        tests: []
    };
};

/**
 * Extract value from skGS1pk string (from old helper)
 * This is kept for compatibility with test data structures
 */
export function extractValue(str: string, fieldName: string): string | null {
    const parts = str.split("#");
    for (const part of parts) {
        if (part.startsWith(fieldName + ":")) {
            return part.substring(fieldName.length + 1);
        }
    }
    return null;
}
